import { Children, Fragment, isValidElement, type ReactNode } from "react";
import { useTranslate } from "@refinedev/core";

import { COMPONENT_PER_SLEUTEL, type ComponentStatus } from "./dierComponenten";
import { RING_KLEUR, type Dichtheid } from "./Gezondheidsring";

/**
 * De tegelvormen uit het ontwerp van 30-09-2026.
 *
 * <p><b>Waarom dit vormen zijn en geen tegels.</b> Het ontwerp zegt het zelf: "een tegelontwerp bepaalt
 * alleen de presentatie; het invoerscherm en de onderliggende registratie blijven voor ieder onderdeel
 * hetzelfde". Elf voorbeeldtegels bouwen zou elf keer dezelfde onderdelen opnieuw bedraden, en dan
 * verschilt "medicatie" per tegel zodra iemand er een aanpast. Hier staan alleen de vormen; welke
 * onderdelen erin gaan zegt de tegel, en wat een onderdeel betekent zegt de componentbibliotheek.
 *
 * <p>Daardoor werkt elke vorm ook met onderdelen waarvan de gegevens nog niet bestaan: die zeggen dan
 * "nog niets geregistreerd", wat waar is, in plaats van dat de vorm niet te bouwen zou zijn.
 *
 * <p>Elke vorm neemt dezelfde drie dingen aan: een <b>kern</b> (het middelpunt, of leeg), de
 * <b>onderdelen</b> en een <b>dichtheid</b>. Klikken op de achtergrond opent het volledige dieroverzicht;
 * de knoppen van de onderdelen houden hun klik bij zich, zodat een plus niet ook de pagina opent.
 */

export interface VormProps {
  /** Het middelpunt: een groot getal, een naam, een prijs. Leeg mag. */
  kern?: ReactNode;
  /** De onderdelen, al gerenderd door de tegel die deze vorm gebruikt. */
  onderdelen?: ReactNode;
  dichtheid?: Dichtheid;
  /** Klik op de achtergrond opent het volledige dieroverzicht. */
  onOpenen?: () => void;
}

/**
 * De omhulling die elke vorm deelt: het klikvlak en de dichtheid.
 *
 * <p>Het klikvlak is een {@code div} met een rol en geen {@code button}, omdat er knoppen in staan. Een
 * knop in een knop is ongeldige HTML en gedraagt zich in elke browser net anders; een rol met
 * toetsafhandeling doet hetzelfde zonder dat probleem.
 */
function Vorm({
  soort,
  dichtheid = "normaal",
  onOpenen,
  children,
}: {
  soort: string;
  dichtheid?: Dichtheid;
  onOpenen?: () => void;
  children: ReactNode;
}) {
  const t = useTranslate();
  return (
    <div
      className={`dt-vorm dt-vorm--${soort}`}
      data-dichtheid={dichtheid}
      data-testid={`vorm-${soort}`}
      onClick={onOpenen}
      role={onOpenen ? "button" : undefined}
      tabIndex={onOpenen ? 0 : undefined}
      onKeyDown={onOpenen ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenen();
        }
      } : undefined}
      aria-label={onOpenen ? t("dierTegel.ring.openen") : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Halve ring: één blokje per gekozen onderdeel, elk met de kleur van zijn eigen toestand.
 *
 * <p><b>Dit verving een boog die niets zei.</b> Hij was voor 60% gevuld met een verzonnen getal en had
 * het woord "voorbeeld" in het midden; op het dashboard stond hij zelfs op nul. Een meter die niets meet
 * is een plaatje dat zich voordoet als informatie.
 *
 * <p>Nu is elk blokje een onderdeel uit de tegel, in dezelfde volgorde als de lijst eronder, met de
 * kleur van dat onderdeel: groen als het in orde is of vastligt, amber als het bijna verloopt, rood als
 * het verlopen is, grijs als er niets over bekend is. In het midden staat wat je eraan hebt: hoeveel
 * onderdelen aandacht vragen.
 *
 * <p><b>Grijs is geen oordeel.</b> Een dier dat nooit gewogen is telt niet als probleem en niet als in
 * orde -- er is alleen niets bekend. Zou grijs meetellen als aandachtspunt, dan staat elk nieuw dier
 * meteen in het rood; zou het meetellen als in orde, dan lijkt een leeg dossier af.
 */
export interface MeterSegment {
  sleutel: string;
  label: string;
  status: ComponentStatus | "onbekend";
}

/**
 * Halve meter: dezelfde tegel als Rondom, met een halve ring.
 *
 * <p>Uit het ontwerp: de drie stijlkaartjes tonen één compositie -- de foto in het midden met de
 * waarden eromheen -- en verschillen alleen in de vorm van de ring. Een boog met een lijstje eronder
 * is een andere tegel, en dan betekent "stijl" ineens iets anders per keuze.
 */
export function HalveMeter({
  segmenten,
  onderdelen,
  foto,
  dichtheid,
  onOpenen,
}: VormProps & { segmenten: MeterSegment[]; foto?: string | null }) {
  return (
    <Rondom half segmenten={segmenten} onderdelen={onderdelen} foto={foto} dichtheid={dichtheid}
      onOpenen={onOpenen} />
  );
}

/**
 * Rondom: de foto in het midden, de waarden eromheen, elk vast aan zijn eigen ringsegment.
 *
 * <p>Naar het ontwerp van 02-10-2026. Een ring met een lijstje eronder dwingt je zelf uit te zoeken
 * welk segment bij welk onderdeel hoort, en dan zegt die kleur niets. Hier staat elk onderdeel náást
 * zijn segment, met een icoonschijfje op de ring op precies die plek: de kleur van het segment, het
 * schijfje en het streepje bij de waarde zijn dezelfde kleur, dus het verband is te zien en niet te
 * beredeneren.
 *
 * <p>De onderdelen staan om en om links en rechts, van boven naar beneden. Het segment van een
 * onderdeel ligt gecentreerd op dezelfde hoek als zijn schijfje; de gaten vallen daardoor vanzelf
 * boven, onder en opzij.
 */
/**
 * De foto van het dier als rond midden, voor de vormen die een kern hebben.
 *
 * <p>Zo kan "toon foto" overal hetzelfde betekenen. De ronde tegel en de halve meter tekenen hun foto
 * zelf, want daar zit hij binnen de ring en moet hij op die maat passen.
 */
export function Fotokern({ foto }: { foto: string }) {
  return <img className="dt-vorm__fotokern" src={foto} alt="" />;
}

export function Rondom({
  segmenten,
  onderdelen,
  foto,
  half,
  dichtheid,
  onOpenen,
}: VormProps & { segmenten: MeterSegment[]; foto?: string | null; half?: boolean }) {
  const t = useTranslate();
  const items = platteOnderdelen(onderdelen);
  const aantal = Math.max(1, items.length);
  const rijen = Math.ceil(aantal / 2);

  // De hoek van onderdeel i, in graden, met 0 rechts en met de klok mee (y wijst omlaag in svg).
  // Rechts loopt van -60 (boven) naar 60 (onder); links is daar de spiegeling van.
  const hoekVan = (i: number) => {
    const deel = (Math.floor(i / 2) + 0.5) / rijen;
    const rechts = -60 + 120 * deel;
    return i % 2 === 0 ? 180 - rechts : rechts;
  };
  // Bij de halve variant liggen de stukken op de bovenste helft: van links (180°) over de top naar
  // rechts (360°). De waarden blijven links en rechts staan; de kleur verbindt ze met hun stuk.
  const hoekHalf = (i: number) => 180 + ((i + 0.5) * 180) / aantal;
  const hoeken = items.map((_, i) => (half ? hoekHalf(i) : hoekVan(i)));

  // Hoe breed een segment mag worden zonder zijn buurman te raken: de kleinste hoekafstand tot een
  // ander onderdeel. Met twee onderdelen vlak bij elkaar worden het dus twee smalle boogjes in plaats
  // van twee die over elkaar heen lopen.
  const afstandTot = (i: number) => {
    const anderen = hoeken.filter((_, j) => j !== i);
    if (anderen.length === 0) {
      return 360;
    }
    // Het verschil teruggebracht tot 0..180: twee onderdelen recht tegenover elkaar staan 180 uit
    // elkaar, niet 0. Met die twee verwisseld werd de smalste afstand nul en tekende elk segment een
    // boog van niets -- de ring had dan alleen zijn grijze spoor.
    return Math.min(...anderen.map((h) => Math.abs(((h - hoeken[i] + 540) % 360) - 180)));
  };

  const straal = 44;
  const omtrek = 2 * Math.PI * straal;
  const rad = (graden: number) => (graden * Math.PI) / 180;

  return (
    <Vorm soort="rondom" dichtheid={dichtheid} onOpenen={onOpenen}>
      <div className="dt-rondom" data-rijen={rijen}>
        <div className="dt-rondom__kolom">
          {items.map((kind, i) => (i % 2 === 0 ? <Rondstuk key={i} kind={kind} segment={segmenten[i]} /> : null))}
        </div>

        <div className="dt-rondom__cirkel">
          <svg viewBox="0 0 100 100" role="img" aria-hidden="true">
            {half ? (
              <path d="M 6 50 A 44 44 0 0 1 94 50" fill="none" stroke="var(--fawkes-color-border)"
                strokeWidth="5" strokeLinecap="round" opacity="0.3" />
            ) : (
              <circle cx="50" cy="50" r={straal} fill="none" stroke="var(--fawkes-color-border)"
                strokeWidth="5" opacity="0.3" />
            )}
            {items.map((_, i) => {
              const segment = segmenten[i];
              const breedte = half
                ? (180 / aantal) * 0.8
                : Math.min(360 / aantal, afstandTot(i)) * 0.82;
              const lengte = (breedte / 360) * omtrek;
              const start = hoeken[i] - breedte / 2;
              // De halve boog begint linksonder; de afstand langs het pad telt vanaf 180 graden.
              const verschuiving = half ? ((start - 180) / 360) * omtrek : rad(start) * straal;
              return half ? (
                <path
                  key={i}
                  d="M 6 50 A 44 44 0 0 1 94 50"
                  fill="none"
                  stroke={RING_KLEUR[segment?.status ?? "onbekend"]}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={`${lengte} ${omtrek}`}
                  strokeDashoffset={-verschuiving}
                >
                  <title>
                    {`${segment?.label ?? ""}: ${t(`dierTegel.ring.status.${segment?.status ?? "onbekend"}`)}`}
                  </title>
                </path>
              ) : (
                <circle
                  key={i}
                  cx="50"
                  cy="50"
                  r={straal}
                  fill="none"
                  stroke={RING_KLEUR[segment?.status ?? "onbekend"]}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={`${lengte} ${omtrek}`}
                  strokeDashoffset={-rad(start) * straal}
                >
                  <title>
                    {`${segment?.label ?? ""}: ${t(`dierTegel.ring.status.${segment?.status ?? "onbekend"}`)}`}
                  </title>
                </circle>
              );
            })}
          </svg>

          {foto ? <img className="dt-rondom__foto" src={foto} alt="" /> : <div className="dt-rondom__foto" />}

          {/* Het schijfje op de ring, op de hoek van zijn eigen segment: dat is wat de waarde aan de
              ring vastknoopt. */}
          {items.map((_, i) => {
            const segment = segmenten[i];
            const def = segment ? COMPONENT_PER_SLEUTEL[segment.sleutel] : undefined;
            const Icoon = def?.icoon;
            return (
              <span
                key={i}
                className="dt-rondom__schijf"
                style={{
                  left: `${50 + straal * Math.cos(rad(hoeken[i]))}%`,
                  top: `${50 + straal * Math.sin(rad(hoeken[i]))}%`,
                  ["--dt-rondkleur" as string]: RING_KLEUR[segment?.status ?? "onbekend"],
                }}
                aria-hidden="true"
              >
                {Icoon ? <Icoon size={13} /> : null}
              </span>
            );
          })}
        </div>

        <div className="dt-rondom__kolom dt-rondom__kolom--rechts">
          {items.map((kind, i) => (i % 2 === 1 ? <Rondstuk key={i} kind={kind} segment={segmenten[i]} /> : null))}
        </div>
      </div>
    </Vorm>
  );
}

function Rondstuk({ kind, segment }: { kind: ReactNode; segment?: MeterSegment }) {
  return (
    <div
      className="dt-rondom__stuk"
      data-status={segment?.status ?? "onbekend"}
      style={{ ["--dt-rondkleur" as string]: RING_KLEUR[segment?.status ?? "onbekend"] }}
    >
      {kind}
    </div>
  );
}

/**
 * Boogmeter: dezelfde halve ring, compact en zonder lijst.
 *
 * <p>Het ontwerp zet hem op de Aandachtsmeter, waar één blik telt. Alles eronder zou de ring tot
 * versiering maken.
 */
export function Boogmeter({ segmenten, onOpenen }: VormProps & { segmenten: MeterSegment[] }) {
  const t = useTranslate();
  const aantal = Math.max(1, segmenten.length);
  const omtrek = 2 * Math.PI * 42;
  const breedte = (180 / aantal) * 0.8;

  const aandacht = segmenten.filter((s) => s.status === "verlopen" || s.status === "aandacht").length;
  const bekend = segmenten.filter((s) => s.status !== "onbekend").length;
  const samenvatting =
    aandacht > 0
      ? t("dierTegel.meter.aandacht", { args: [aandacht] })
      : bekend === 0
        ? t("dierTegel.meter.nietsBekend")
        : t("dierTegel.meter.inOrde");

  return (
    <Vorm soort="boogmeter" dichtheid="compact" onOpenen={onOpenen}>
      <div className="dt-vorm__meter">
        <svg viewBox="0 0 100 58" role="img" aria-label={samenvatting}>
          <path d="M 8 50 A 42 42 0 0 1 92 50" fill="none" stroke="var(--fawkes-color-border)"
            strokeWidth="9" strokeLinecap="round" opacity="0.35" />
          {segmenten.map((seg, i) => {
            const midden = 180 + ((i + 0.5) * 180) / aantal;
            const lengte = (breedte / 360) * omtrek;
            const vanaf = ((midden - breedte / 2 - 180) / 360) * omtrek;
            return (
              <path
                key={seg.sleutel}
                d="M 8 50 A 42 42 0 0 1 92 50"
                fill="none"
                stroke={RING_KLEUR[seg.status]}
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={`${lengte} ${omtrek}`}
                strokeDashoffset={-vanaf}
              >
                <title>{`${seg.label}: ${t(`dierTegel.ring.status.${seg.status}`)}`}</title>
              </path>
            );
          })}
        </svg>
        <div className="dt-vorm__meterkern">
          <strong>{samenvatting}</strong>
          {bekend < segmenten.length && (
            <span>{t("dierTegel.meter.onbekend", { args: [segmenten.length - bekend] })}</span>
          )}
        </div>
      </div>
    </Vorm>
  );
}

/**
 * Waardenboog: de waarden staan ín de boog, niet eronder.
 *
 * <p>Het verschil met de halve meter is niet cosmetisch. Daar is de boog een <em>meter</em> die iets
 * meet en staan de waarden eronder als lijst. Hier is de boog een <em>rangschikking</em>: de onderdelen
 * liggen erop, van links naar rechts, met de middelste het hoogst. Dat past bij een tegel waar de
 * onderdelen even zwaar wegen en je ze in één blik wilt aflopen.
 *
 * <p><b>Waarom met marges en niet met hoeken.</b> Elk onderdeel zakt ten opzichte van het midden volgens
 * een parabool. Dat levert dezelfde boog op als rekenen met sinussen, maar blijft werken bij elke
 * breedte en elk aantal onderdelen, en de tekst blijft rechtop -- gedraaide tekst in een tegel leest
 * niemand. Wordt de tegel te smal, dan vlakt de boog vanzelf af tot een lijst, en dat is beter dan
 * onderdelen die over elkaar schuiven.
 */
/**
 * De onderdelen als platte lijst, door fragmenten heen.
 *
 * <p>Nodig omdat de tegels hun onderdelen als één fragment doorgeven ({@code <>{...map()}</>}).
 * {@code Children.toArray} ziet dat als één kind, en dan legt de boog alles op dezelfde plek in plaats
 * van naast elkaar. Elke andere vorm merkt daar niets van, want die rendert ze gewoon door; alleen deze
 * moet ze kunnen tellen.
 */
function platteOnderdelen(node: ReactNode): ReactNode[] {
  return Children.toArray(node).flatMap((kind) =>
    isValidElement(kind) && kind.type === Fragment
      ? platteOnderdelen((kind.props as { children?: ReactNode }).children)
      : [kind]);
}

export function Waardenboog({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  const items = platteOnderdelen(onderdelen);
  // Hoe diep de boog doorhangt: genoeg om hem te zien, weinig genoeg om de tegel niet twee keer zo hoog
  // te maken als zijn inhoud.
  const diepte = dichtheid === "compact" ? 10 : 18;
  const midden = (items.length - 1) / 2;
  return (
    <Vorm soort="waardenboog" dichtheid={dichtheid} onOpenen={onOpenen}>
      {kern ? <div className="dt-vorm__kern">{kern}</div> : null}
      <div className="dt-vorm__boog">
        {items.map((kind, i) => {
          // Een parabool door het midden: de buitenste onderdelen zakken het diepst.
          const afstand = midden === 0 ? 0 : (i - midden) / midden;
          const zak = Math.round(afstand * afstand * diepte);
          return (
            <div key={i} className="dt-vorm__boogitem" style={{ marginTop: zak + "px" }}>
              {kind}
            </div>
          );
        })}
      </div>
    </Vorm>
  );
}

/**
 * Satellieten: een kern met de onderdelen eromheen.
 *
 * <p>Voor een tegel waar de onderdelen bij elkaar horen zonder rangorde -- ras, leeftijd, geslacht, chip.
 * Ze staan in een raster om de kern en niet op een cirkel: een cirkel dwingt een vaste plek per
 * onderdeel, en dan verspringt alles zodra de gebruiker er een weghaalt.
 */
export function Satellieten({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="satellieten" dichtheid={dichtheid} onOpenen={onOpenen}>
      {kern ? <div className="dt-vorm__kern">{kern}</div> : null}
      <div className="dt-vorm__satellieten">{onderdelen}</div>
    </Vorm>
  );
}

/**
 * Gesplitst: twee helften naast elkaar, kern links en onderdelen rechts.
 *
 * <p>Voor een tegel met twee gezichten -- voeding en gewicht -- waar geen van beide de ander mag
 * verdringen. Bij compact gaan ze onder elkaar in plaats van te versmallen: twee kolommen van vijftig
 * pixels leest niemand.
 */
export function Gesplitst({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="gesplitst" dichtheid={dichtheid} onOpenen={onOpenen}>
      <div className="dt-vorm__helft">{kern}</div>
      <div className="dt-vorm__helft">{onderdelen}</div>
    </Vorm>
  );
}

/**
 * Statuslijst: de onderdelen onder elkaar, elk met zijn eigen status.
 *
 * <p>De rustigste vorm, en daarmee de juiste voor medische veiligheid: allergieën en medicatie lees je
 * regel voor regel en niet in een oogopslag. Wat hier telt is dat je niets overslaat, niet dat het snel
 * gaat.
 */
export function Statuslijst({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="statuslijst" dichtheid={dichtheid} onOpenen={onOpenen}>
      {kern ? <div className="dt-vorm__kop">{kern}</div> : null}
      <div className="dt-vorm__lijst">{onderdelen}</div>
    </Vorm>
  );
}

/**
 * Radiaal: de onderdelen als spaken om een kern.
 *
 * <p>Voor gedrag en sociale geschiktheid: vier of vijf oordelen die even zwaar wegen en samen een beeld
 * geven. Anders dan bij Satellieten hoort hier wél een middelpunt, want de spaken gaan ergens over --
 * het dier -- en zonder dat middelpunt is het een losse verzameling.
 */
export function Radiaal({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="radiaal" dichtheid={dichtheid} onOpenen={onOpenen}>
      <div className="dt-vorm__naaf">{kern}</div>
      <div className="dt-vorm__spaken">{onderdelen}</div>
    </Vorm>
  );
}

/**
 * Prijskaart: een bedrag groot, met de opbouw eronder.
 *
 * <p>De enige vorm waar de kern een bedrag is. Die staat groot omdat "wat kost dit dier per dag" de
 * vraag is; de opbouw eronder is het antwoord op "waarom", en dat leest iemand pas als het eerste getal
 * hem verbaast.
 */
export function Prijskaart({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="prijskaart" dichtheid={dichtheid} onOpenen={onOpenen}>
      <div className="dt-vorm__bedrag">{kern}</div>
      {onderdelen ? <div className="dt-vorm__lijst">{onderdelen}</div> : null}
    </Vorm>
  );
}

/**
 * Twee kolommen: de onderdelen verdeeld over twee kolommen.
 *
 * <p>Voor een brede tegel met twee groepen die los van elkaar te lezen zijn -- identiteit links,
 * gezondheid rechts. De verdeling gebeurt in CSS en niet in code: welke onderdelen er zijn, bepaalt de
 * tegel, en die hoeft niet te weten waar de vouw valt.
 */
export function TweeKolommen({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="tweekolommen" dichtheid={dichtheid} onOpenen={onOpenen}>
      {kern ? <div className="dt-vorm__kop">{kern}</div> : null}
      <div className="dt-vorm__kolommen">{onderdelen}</div>
    </Vorm>
  );
}

/**
 * Kwadranten: vier vakken van gelijke grootte.
 *
 * <p>Het Dierkompas: voeding, gezondheid, gedrag en verzorging, elk even belangrijk. Vier gelijke vakken
 * zeggen dat ook -- zou er één groter zijn, dan leest dat als een rangorde die er niet is.
 */
export function Kwadranten({ kern, onderdelen, dichtheid, onOpenen }: VormProps) {
  return (
    <Vorm soort="kwadranten" dichtheid={dichtheid} onOpenen={onOpenen}>
      {kern ? <div className="dt-vorm__kop">{kern}</div> : null}
      <div className="dt-vorm__kwadranten">{onderdelen}</div>
    </Vorm>
  );
}
